// src/lib/stats-aggregate.js — Lot 0 onglet Statistiques proprio.
//
// ⚠️ MODULE PUR — ZÉRO I/O. Aucun appel Supabase / réseau / React ici.
// Moteur d'AGRÉGATION : il reçoit des données DÉJÀ projetées et keyées par mois,
// il agrège ; il N'IMPUTE PAS lui-même (l'imputation « majorité de nuits » est
// faite en amont par projectDataForFacturation / le hook pricing-blocks).
//
// ── CONTRAT D'INTERFACE (décisions Dimitri + pessimist) ──
//
// aggregateStats({ reservationsByLidMk, directBlocksByLidMk, logsByLid, period, nbLogements })
//
//   reservationsByLidMk : { [lid]: { [mk "YYYY-MM"]: Array<resa projetée> } }
//     Les résas sont le PROJETÉ ÉDITÉ (Précision #2) — montants corrigés
//     (applyMontantOverrides), supprimées exclues (applyTombstones), déplacées
//     au bon mois (applyMonthMoves), manuelles incluses. JAMAIS les rows brutes.
//     Chaque résa porte (estampés Lot 0) : montant, taxe, menage, nuits,
//     plateforme, checkIn/checkOut, ca_brut_origine, ota_commission, montant_origine.
//
//   directBlocksByLidMk : Map<mk, Map<lid, Array<block>>> — la MÊME Map que celle
//     produite par le hook use-pricing-blocks-history (blocksByMonth), DÉJÀ filtrée
//     (active=true + guest_first_name non null + total_amount non null, règle
//     majorité-de-nuits appliquée). D4 : le moteur NE requête JAMAIS pricing_blocks
//     lui-même — il délègue à calcDirectInternePure (source unique du direct interne).
//
//   logsByLid : { [lid]: logDef } — la définition logement (taux, commissionMode,
//     paliers…) nécessaire à buildFacturationLignes (source UNIQUE commission FBZ)
//     et au taux direct interne.
//
//   period : Array<mk "YYYY-MM"> — l'ensemble des mois à agréger (ordre conservé).
//
//   nbLogements : number — nombre d'unités louables DISTINCTES du périmètre
//     (sert nuitsDispo = jours × nbLogements ; multi-logement recomposé sur les Σ).
//
// Retour : { parMois, total, kpis, parPlateforme } — tous montants round2 À LA FIN
// (D6 : on somme les composantes NON arrondies par résa, round2 seulement sur
//  l'agrégat, pour éviter les écarts d'1 centime).

import { calcDirectInternePure, isSousLocLid } from './calc'
import { buildFacturationLignes } from './facturation-lignes'
import { resoudreCommission } from './commission-taux-date'
import { daysInMonth } from './format'
import { nightsInMonth, daysInMonthMk } from './resa-invoice-month'

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100
}

// Division sûre : jamais NaN ni Infinity (edge case /0 → 0).
function safeDiv(num, den) {
  if (!den || !Number.isFinite(den) || den === 0) return 0
  const r = num / den
  return Number.isFinite(r) ? r : 0
}

// Jours calendaires d'un mois "YYYY-MM" (daysInMonth attend m 1-indexé).
function joursDuMois(mk) {
  const m = /^(\d{4})-(\d{2})$/.exec(mk || '')
  if (!m) return 0
  return daysInMonth(Number(m[1]), Number(m[2]))
}

// ── bucketPlateforme(plateforme) (Précision #1) ──
// airbnb→airbnb ; booking→booking ; direct|manuel→direct. Pas de 4e catégorie.
export function bucketPlateforme(plateforme) {
  const p = String(plateforme || '').toLowerCase()
  if (p === 'airbnb') return 'airbnb'
  if (p === 'booking') return 'booking'
  // direct, manuel, et tout le reste → bucket Direct.
  return 'direct'
}

// ── caBrutResa(resa) (mode UNIQUE origine_plus_delta, décision Dimitri) ──
// caBrut = ca_brut_origine + (montant − montant_origine).
// Le CA brut suit la correction de montant (Lot 1) au centime, ancré sur le vrai
// brut OTA. Fallbacks DÉFENSIFS (D1) si les champs origine manquent (résa pré-Lot 0
// ou manuelle non estampée) :
//   caBrutOrigine = ca_brut_origine ?? (montant + ota_commission + taxe)
//   montantOrigine = montant_origine ?? montant
// NON arrondi ici (round2 à l'agrégat — D6).
function caBrutResa(r) {
  const montant = Number(r.montant) || 0
  const taxe = Number(r.taxe) || 0
  const ota = Number(r.ota_commission) || 0
  const caBrutOrigine =
    r.ca_brut_origine != null ? Number(r.ca_brut_origine) : montant + ota + taxe
  const montantOrigine =
    r.montant_origine != null ? Number(r.montant_origine) : montant
  return caBrutOrigine + (montant - montantOrigine)
}

// ── isDateInPeriod(iso, periodSet) ── (D5) date réelle ∈ un mois de la période.
function mkOfDate(iso) {
  return typeof iso === 'string' && /^\d{4}-\d{2}/.test(iso) ? iso.slice(0, 7) : null
}

// Accumulateur vide d'un mois (toutes composantes en SOMMES non arrondies).
function emptyAcc() {
  return {
    nuitsLouees: 0, // TOTAL imputé (Σ r.nuits bucket + direct) → nuitsMoyResa
    nuitsReel: 0, // Option B : nuits RÉELLES occupées ce mois → occupation
    caBrut: 0,
    net: 0, // Σ montant (OTA) + Σ ca direct
    netOta: 0, // Σ montant des résas OTA/manuelles (revenusNets KPI)
    commissionsOta: 0,
    taxe: 0,
    menage: 0,
    commissionFbz: 0,
    nbResa: 0,
    // ── Lot 2 ──
    // nuitsBloquees : blocs FBZ PURS (guest null + total null), réduisent le
    //   dénominateur d'occupation hors-bloqué. SOMME BRUTE (bornage à la fin).
    // nuitsSansMontant : cat.3 (guest non null + total null), comptées OCCUPÉES
    //   (numérateur) + surfacées en indicateur.
    nuitsBloquees: 0,
    nuitsSansMontant: 0,
    // nbVoyageurs : Σ + count des résas RENSEIGNÉES (nbVoyageurs non null).
    nbVoyageursSomme: 0,
    nbVoyageursCount: 0,
    pf: {
      airbnb: { nbResa: 0, caBrut: 0 },
      booking: { nbResa: 0, caBrut: 0 },
      direct: { nbResa: 0, caBrut: 0 },
    },
  }
}

// Σ des nuits d'un Map<mk, Map<lid, nights>> pour un mk donné sur un set de lids.
// Utilisé pour nuitsBloquees / nuitsSansMontant (imputation majorité-nuits faite
// en amont à l'endpoint, ici on somme simplement). null/absent → 0.
function sumNightsForMonth(byLidMk, mk, lidSet) {
  if (!byLidMk || typeof byLidMk.get !== 'function') return 0
  const monthMap = byLidMk.get(mk)
  if (!monthMap || typeof monthMap.forEach !== 'function') return 0
  let s = 0
  monthMap.forEach((nights, lid) => {
    if (lidSet.has(lid)) s += Number(nights) || 0
  })
  return s
}

// ── computeOccupation(...) (Lot 2) ──
// Calcule occupation, occupationHorsBloque, nuitsBloquees(effective) avec bornage.
//   occupiedNuits = nuitsLouees + nuitsSansMontant (cat.3 comptée occupée).
//   occupation = occupiedNuits / nuitsDispo × 100  (null si dispo=0).
//   nuitsBloquees_effective = min(nuitsBloquees, max(0, dispo − occupiedNuits))
//     → dénominateur hors-bloqué jamais ≤ 0, occupation jamais > 100.
//   occupationHorsBloque = occupiedNuits / (dispo − nuitsBloquees_eff) × 100
//     (null si dispo=0 OU dénominateur ≤ 0).
// Si le brut nuitsBloquees dépassait la marge → onOverflow() (log sans PII).
function computeOccupation({
  nuitsLouees,
  nuitsSansMontant,
  nuitsBloquees,
  nuitsDispo,
  hasV2,
  onOverflow,
}) {
  const occupiedNuits = (Number(nuitsLouees) || 0) + (Number(nuitsSansMontant) || 0)
  const dispo = Number(nuitsDispo) || 0
  const occupation = dispo > 0 ? round2((occupiedNuits / dispo) * 100) : null

  // En KV (!hasV2) : pas de cat.3 ni de blocs → occupation = nuitsLouees/dispo.
  // occupationHorsBloque / nuitsBloquees seront nullifiés par degradeKvFields.
  const bloqueBrut = Number(nuitsBloquees) || 0
  const marge = Math.max(0, dispo - occupiedNuits)
  if (hasV2 && bloqueBrut > marge && typeof onOverflow === 'function') {
    onOverflow()
  }
  const bloqueEff = Math.min(bloqueBrut, marge)
  const denomHorsBloque = dispo - bloqueEff
  const occupationHorsBloque =
    dispo > 0 && denomHorsBloque > 0
      ? round2((occupiedNuits / denomHorsBloque) * 100)
      : null

  return { occupation, occupationHorsBloque, nuitsBloqueesEff: bloqueEff }
}

// ── aggregateStats(...) ──
export function aggregateStats({
  reservationsByLidMk,
  directBlocksByLidMk,
  logsByLid,
  period,
  nbLogements,
  // ── Lot 2 (additifs, défaut null = mode KV dégradé côté endpoint) ──
  blockedNightsByLidMk = null, // Map<mk,Map<lid,nights>> blocs FBZ purs
  occupiedUnpricedByLidMk = null, // Map<mk,Map<lid,nights>> cat.3
  logError = null, // injection optionnelle (overflow donnée sale)
  // ── Option B (occupation/nuits-louées en « nuits réelles par mois ») ──
  // allResasByLid : { [lid]: Array<résa projetée> } NON bucketée — TOUTES les
  // résas du lid (tous mois confondus). Permet de compter les nuits d'une résa
  // À CHEVAL dans CHAQUE mois qu'elle touche (une résa de 83 nuits ne vit que
  // dans UN bucket = son mois majoritaire ; le bucket seul ne suffit pas).
  // null/absent → fallback historique (nuitsLouees = Σ r.nuits du bucket =
  // ancienne sémantique majorité-nuits, 0-régression des tests existants).
  allResasByLid = null,
  // ── Lot Statistiques (mesure 3, 2026-09) — dénominateur d'occupation PAR MOIS ──
  // nbLogementsByMk : { [mk]: number } — nombre d'unités EN SERVICE ce mois-là
  // (fourni par buildAdminStatsInputs : un logement compte à partir de son
  // premier mois d'activité). null/absent → fallback historique nbLogements
  // constant (0-régression : le serveur proprio ne le passe jamais).
  nbLogementsByMk = null,
} = {}) {
  const months = Array.isArray(period) ? period : []
  const lids = Object.keys(reservationsByLidMk || {})
  // Le direct interne vit aussi sur des lids qui peuvent ne pas avoir de résas
  // KV/OTA sur la période → réunir les lids des deux sources.
  const directLids = new Set()
  if (directBlocksByLidMk && typeof directBlocksByLidMk.forEach === 'function') {
    directBlocksByLidMk.forEach((monthMap) => {
      if (monthMap && typeof monthMap.forEach === 'function') {
        monthMap.forEach((_blocks, lid) => directLids.add(lid))
      }
    })
  }
  const allLids = Array.from(new Set([...lids, ...directLids]))
  const allLidsSet = new Set(allLids)
  const nbLog = Number(nbLogements) > 0 ? Number(nbLogements) : 0
  // hasV2 (Lot 2) : présence des maps blocs/cat.3 (passées Map si V2, null si KV).
  const hasV2 =
    !!blockedNightsByLidMk || !!occupiedUnpricedByLidMk
  const reportOverflow = (mk) => {
    if (typeof logError === 'function') {
      logError('proprio-stats.blocked_overflow', 'nuitsBloquees overflow', {
        month: mk,
        lidCount: allLids.length,
      })
    }
  }
  // Option B : occupation réelle dispo ssi allResasByLid fourni (sinon fallback
  // historique = Σ r.nuits du bucket).
  const useRealNights = !!allResasByLid && typeof allResasByLid === 'object'

  const parMois = {}
  // KPIs d'activité opérationnelle (D5) : sur les DATES RÉELLES, distinct du mois
  // d'imputation €. Un check-in/out compte s'il tombe dans un mois de la période.
  const periodSet = new Set(months)
  let nbCheckin = 0
  let nbCheckout = 0
  // Dédup anti-bruit occup_overflow : une unité double-bookée déborde sur CHAQUE
  // mois de la période → sans garde, Sentry reçoit N events identiques par
  // chargement de dashboard. On ne loggue qu'une fois par lid et par agrégation.
  const occupOverflowLoggedLids = new Set()

  for (const mk of months) {
    const acc = emptyAcc()
    // Option B : nuits RÉELLES occupées ce mois (Σ_lid Σ_resa nightsInMonth),
    // par scan de allResasByLid (toutes les résas du lid, tous mois) — une résa
    // à cheval compte dans CHAQUE mois touché, plafonnée par lid à daysInMonth.
    let nuitsReelMois = 0
    const joursMk = daysInMonthMk(mk)

    for (const lid of allLids) {
      const log = (logsByLid || {})[lid] || null
      // Taux DATÉ (lot taux-date) : ce `taux` plat n'alimente QUE le direct
      // interne (calcDirectInternePure) — la commission OTA passe, elle, par
      // buildFacturationLignes qui résout le barème par résa. Sans
      // `tauxHistorique`, on lit le champ plat comme avant (`log.taux` est ici
      // déjà le taux effectif aplati par l'appelant, fallback parent compris).
      const baremeLog = resoudreCommission(log, mk)
      const taux = baremeLog.historise
        ? (Number.isFinite(Number(baremeLog.taux)) ? Number(baremeLog.taux) : 0)
        : (log && log.taux != null ? log.taux : 0)
      const sousLoc = isSousLocLid(lid)

      // ── Option B : nuits réelles du lid dans mk (avant blocs/direct) ──
      if (useRealNights) {
        const resasLid = Array.isArray(allResasByLid[lid]) ? allResasByLid[lid] : []
        let lidNights = 0
        for (const r of resasLid) {
          if (!r) continue
          lidNights += nightsInMonth(r, mk)
        }
        // Plafond DUR par unité (1 logement ≤ daysInMonth nuits/mois). Si la
        // donnée est sale (double-booking, chevauchement) → log AVANT plafonnement.
        if (joursMk > 0 && lidNights > joursMk) {
          // 1 seul log par lid/agrégation (le 1er mois débordant). Le plafonnement
          // métier reste inconditionnel — jamais court-circuité par la dédup.
          if (typeof logError === 'function' && !occupOverflowLoggedLids.has(lid)) {
            occupOverflowLoggedLids.add(lid)
            logError('proprio-stats.occup_overflow', 'occupation overflow (donnée sale)', {
              lid,
              mk,
              raw: lidNights,
            })
          }
          lidNights = joursMk
        }
        nuitsReelMois += lidNights
      }

      // ── 1) Résas OTA/manuelles projetées (reservationsByLidMk) ──
      const resas =
        (reservationsByLidMk &&
          reservationsByLidMk[lid] &&
          reservationsByLidMk[lid][mk]) ||
        []

      for (const r of resas) {
        if (!r) continue
        acc.nbResa += 1
        acc.nuitsLouees += Number(r.nuits) || 0
        const montant = Number(r.montant) || 0
        acc.net += montant
        acc.netOta += montant
        acc.taxe += Number(r.taxe) || 0
        acc.menage += Number(r.menage) || 0
        acc.commissionsOta += Number(r.ota_commission) || 0
        // nbVoyageurs (Lot 2) : moyenne sur les résas RENSEIGNÉES uniquement
        // (null = non renseigné → exclu du count, jamais compté 0).
        if (r.nbVoyageurs != null && Number.isFinite(Number(r.nbVoyageurs))) {
          acc.nbVoyageursSomme += Number(r.nbVoyageurs)
          acc.nbVoyageursCount += 1
        }
        const cb = caBrutResa(r)
        acc.caBrut += cb
        const bucket = bucketPlateforme(r.plateforme)
        acc.pf[bucket].nbResa += 1
        acc.pf[bucket].caBrut += cb

        // Activité opérationnelle (D5) sur dates réelles.
        const ci = mkOfDate(r.checkIn || r.start_date || r.date)
        const co = mkOfDate(r.checkOut || r.end_date)
        if (ci && periodSet.has(ci)) nbCheckin += 1
        if (co && periodSet.has(co)) nbCheckout += 1
      }

      // Commission FBZ des résas OTA/manuelles via la SOURCE UNIQUE
      // buildFacturationLignes (0 sous-loc). Sommée non arrondie (D6).
      if (!sousLoc && resas.length) {
        const lignes = buildFacturationLignes(resas, log, mk)
        for (const l of lignes) acc.commissionFbz += Number(l.commission) || 0
      }

      // ── 2) Direct interne (pricing_blocks) via calcDirectInternePure (D4) ──
      // brut = net, otaComm = 0, comm = ca×taux (0 sous-loc). PAS de double
      // comptage : direct interne vit en table séparée, jamais dans les résas KV.
      const dc = calcDirectInternePure(directBlocksByLidMk, lid, taux, mk)
      if (dc && dc.nbResa) {
        acc.nbResa += dc.nbResa
        acc.nuitsLouees += dc.nuits || 0
        // Option B : le direct interne (blocs intra-mois, déjà imputés majorité
        // par le hook) occupe aussi — additif à l'occupation réelle.
        if (useRealNights) nuitsReelMois += dc.nuits || 0
        acc.net += dc.ca || 0 // direct interne entre dans le net
        acc.netOta += dc.ca || 0
        acc.caBrut += dc.ca || 0 // brut = net pour le direct
        acc.taxe += dc.taxe || 0 // taxe séjour figée direct → carte « Taxe de séjour »
        acc.commissionFbz += dc.comm || 0 // 0 si sous-loc (calcDirectInternePure)
        acc.pf.direct.nbResa += dc.nbResa
        acc.pf.direct.caBrut += dc.ca || 0

        // Activité opérationnelle des blocks direct (dates réelles).
        for (const br of dc.resas || []) {
          const ci = mkOfDate(br.checkIn || br.start_date)
          const co = mkOfDate(br.end_date)
          if (ci && periodSet.has(ci)) nbCheckin += 1
          if (co && periodSet.has(co)) nbCheckout += 1
        }
      }
    }

    // ── Lot 2 : nuits bloquées (FBZ purs) + cat.3 (occupées sans montant) ──
    acc.nuitsBloquees = sumNightsForMonth(blockedNightsByLidMk, mk, allLidsSet)
    acc.nuitsSansMontant = sumNightsForMonth(occupiedUnpricedByLidMk, mk, allLidsSet)

    const jours = joursDuMois(mk)
    // Mesure 3 : dénominateur PAR MOIS si nbLogementsByMk fourni (unités en
    // service ce mois-là), sinon nbLog constant (fallback historique).
    const nbLogMois =
      nbLogementsByMk && Number.isFinite(Number(nbLogementsByMk[mk]))
        ? Number(nbLogementsByMk[mk])
        : nbLog
    const nuitsDispo = jours * nbLogMois
    // ── Option B — séparation TOTAL vs RÉEL ──
    // acc.nuitsLouees = Σ r.nuits du bucket (+ direct) = TOTAL des résas
    //   IMPUTÉES au mois → sert UNIQUEMENT « nuits moy/résa » (durée séjour).
    // nuitsLouees (affiché) = nuits RÉELLES occupées ce mois (Option B) →
    //   occupation + KPI « Nuits louées » + total affiché.
    const nuitsLouees = useRealNights ? nuitsReelMois : acc.nuitsLouees
    // Mémorise le réel sur _raw pour la recomposition du total (sans toucher
    // acc.nuitsLouees, qui reste le TOTAL imputé pour nuitsMoyResa).
    acc.nuitsReel = nuitsLouees
    const nuitsRestantes = Math.max(0, nuitsDispo - nuitsLouees)
    const occ = computeOccupation({
      nuitsLouees,
      nuitsSansMontant: acc.nuitsSansMontant,
      nuitsBloquees: acc.nuitsBloquees,
      nuitsDispo,
      hasV2,
      onOverflow: () => reportOverflow(mk),
    })
    // netProprio = net − Σ ménage − commFbz (0 sous-loc — pas de proprio).
    // Calculé sur les sommes NON arrondies puis round2.
    // NB : pour un périmètre PUR sous-loc, commFbz et menage agrégés à 0 → on
    // force netProprio à 0 si le périmètre est entièrement sous-loc. En mixte,
    // les sous-loc contribuent 0 commFbz/menage donc n'altèrent pas le net proprio
    // des logements classiques (leur net entre dans acc.net mais sans charge).
    const allSousLoc =
      allLids.length > 0 && allLids.every((lid) => isSousLocLid(lid))
    const netProprio = allSousLoc
      ? 0
      : acc.net - acc.menage - acc.commissionFbz
    const commissionFbz = allSousLoc ? 0 : acc.commissionFbz

    parMois[mk] = {
      nuitsLouees,
      nuitsDispo,
      nuitsRestantes,
      // occupation : numérateur = louées + cat.3 (sansMontant). null si dispo=0.
      occupation: occ.occupation,
      occupationHorsBloque: occ.occupationHorsBloque,
      nuitsBloquees: occ.nuitsBloqueesEff,
      nuitsSansMontant: acc.nuitsSansMontant,
      caBrut: round2(acc.caBrut),
      net: round2(acc.net),
      // revenusNets (« dont ménage » = menage) — exposé par mois (Lot 2). Σ montant
      // (net hôte, ménage compris). Le « dont ménage » est le champ `menage`.
      revenusNets: round2(acc.netOta),
      commissionsOta: round2(acc.commissionsOta),
      taxe: round2(acc.taxe),
      menage: round2(acc.menage),
      commissionFbz: round2(commissionFbz),
      netProprio: round2(netProprio),
      nbResa: acc.nbResa,
      parPlateforme: {
        airbnb: {
          nbResa: acc.pf.airbnb.nbResa,
          caBrut: round2(acc.pf.airbnb.caBrut),
        },
        booking: {
          nbResa: acc.pf.booking.nbResa,
          caBrut: round2(acc.pf.booking.caBrut),
        },
        direct: {
          nbResa: acc.pf.direct.nbResa,
          caBrut: round2(acc.pf.direct.caBrut),
        },
      },
      // Sommes brutes non arrondies (usage interne agrégat total). Non publiées
      // par le rendu mais utiles pour recomposer le total sans re-cumuler les
      // round2 par mois (D6).
      _raw: acc,
    }
  }

  // ── total : Σ sur la période, occupation/RevPAR recomposés sur les Σ ──
  const totalRaw = emptyAcc()
  let totalNuitsDispo = 0
  for (const mk of months) {
    const m = parMois[mk]
    if (!m) continue
    const raw = m._raw
    totalRaw.nuitsLouees += raw.nuitsLouees // TOTAL imputé → nuitsMoyResa
    totalRaw.nuitsReel += raw.nuitsReel // Option B : RÉEL → occupation + affichage
    totalRaw.caBrut += raw.caBrut
    totalRaw.net += raw.net
    totalRaw.netOta += raw.netOta
    totalRaw.commissionsOta += raw.commissionsOta
    totalRaw.taxe += raw.taxe
    totalRaw.menage += raw.menage
    totalRaw.commissionFbz += raw.commissionFbz
    totalRaw.nbResa += raw.nbResa
    // Lot 2 : Σ des nuits cat.3 (brut) + Σ effective des nuits bloquées (déjà
    // bornées par mois → on cumule le rendu m.nuitsBloquees, pas raw brut).
    totalRaw.nuitsSansMontant += raw.nuitsSansMontant
    totalRaw.nuitsBloquees += m.nuitsBloquees
    totalRaw.nbVoyageursSomme += raw.nbVoyageursSomme
    totalRaw.nbVoyageursCount += raw.nbVoyageursCount
    totalRaw.pf.airbnb.nbResa += raw.pf.airbnb.nbResa
    totalRaw.pf.airbnb.caBrut += raw.pf.airbnb.caBrut
    totalRaw.pf.booking.nbResa += raw.pf.booking.nbResa
    totalRaw.pf.booking.caBrut += raw.pf.booking.caBrut
    totalRaw.pf.direct.nbResa += raw.pf.direct.nbResa
    totalRaw.pf.direct.caBrut += raw.pf.direct.caBrut
    totalNuitsDispo += m.nuitsDispo
  }

  // Nettoyer _raw du rendu par mois (interne uniquement).
  for (const mk of Object.keys(parMois)) delete parMois[mk]._raw

  const allSousLocTotal =
    allLids.length > 0 && allLids.every((lid) => isSousLocLid(lid))
  const totalCommFbz = allSousLocTotal ? 0 : totalRaw.commissionFbz
  const totalNetProprio = allSousLocTotal
    ? 0
    : totalRaw.net - totalRaw.menage - totalRaw.commissionFbz

  // Occupation totale (Lot 2 + Option B) : numérateur = nuits RÉELLES + cat.3 ;
  // les nuits bloquées sont DÉJÀ bornées par mois (totalRaw.nuitsBloquees = Σ
  // effective). On ne ré-borne pas (denom hors-bloqué = dispo − ΣbloqueEff,
  // toujours > 0 par construction si dispo>0).
  const totalOccupiedNuits = totalRaw.nuitsReel + totalRaw.nuitsSansMontant
  const totalOccupation =
    totalNuitsDispo > 0
      ? round2((totalOccupiedNuits / totalNuitsDispo) * 100)
      : null
  const totalDenomHorsBloque = totalNuitsDispo - totalRaw.nuitsBloquees
  const totalOccupationHorsBloque =
    totalNuitsDispo > 0 && totalDenomHorsBloque > 0
      ? round2((totalOccupiedNuits / totalDenomHorsBloque) * 100)
      : null

  const total = {
    // nuitsLouees affiché = RÉEL (Option B). nuitsMoyResa garde le TOTAL imputé.
    nuitsLouees: totalRaw.nuitsReel,
    nuitsDispo: totalNuitsDispo,
    nuitsRestantes: Math.max(0, totalNuitsDispo - totalRaw.nuitsReel),
    occupation: totalOccupation,
    occupationHorsBloque: totalOccupationHorsBloque,
    nuitsBloquees: totalRaw.nuitsBloquees,
    nuitsSansMontant: totalRaw.nuitsSansMontant,
    caBrut: round2(totalRaw.caBrut),
    net: round2(totalRaw.net),
    revenusNets: round2(totalRaw.netOta),
    menage: round2(totalRaw.menage),
    commissionsOta: round2(totalRaw.commissionsOta),
    taxe: round2(totalRaw.taxe),
    commissionFbz: round2(totalCommFbz),
    netProprio: round2(totalNetProprio),
    nbResa: totalRaw.nbResa,
    revPAR: round2(safeDiv(totalRaw.caBrut, totalNuitsDispo)),
    parPlateforme: {
      airbnb: {
        nbResa: totalRaw.pf.airbnb.nbResa,
        caBrut: round2(totalRaw.pf.airbnb.caBrut),
      },
      booking: {
        nbResa: totalRaw.pf.booking.nbResa,
        caBrut: round2(totalRaw.pf.booking.caBrut),
      },
      direct: {
        nbResa: totalRaw.pf.direct.nbResa,
        caBrut: round2(totalRaw.pf.direct.caBrut),
      },
    },
  }

  // nbVoyageursMoy (Lot 2) : Σ / count des résas RENSEIGNÉES (count ≠ nbResa).
  // null si aucune résa renseignée (count=0) — jamais 0.
  const nbVoyageursMoy =
    totalRaw.nbVoyageursCount > 0
      ? round2(totalRaw.nbVoyageursSomme / totalRaw.nbVoyageursCount)
      : null

  // ── kpis (8 + activité opérationnelle + Lot 2) ── recomposés sur les Σ.
  const kpis = {
    tauxOccupation: total.occupation,
    nuitsLouees: total.nuitsLouees,
    caBrut: total.caBrut,
    revenusNets: round2(totalRaw.netOta + 0), // = Σ montant net (OTA + direct)
    revPAR: round2(safeDiv(totalRaw.caBrut, totalNuitsDispo)),
    prixMoyenResa: round2(safeDiv(totalRaw.caBrut, totalRaw.nbResa)),
    // nuitsMoyResa = durée moyenne d'un séjour = Σ r.nuits TOTAL des résas
    // imputées / nbResa (PAS l'occupation réelle Option B) — accumulateur séparé.
    nuitsMoyResa: round2(safeDiv(totalRaw.nuitsLouees, totalRaw.nbResa)),
    taxeSejour: total.taxe,
    // Activité opérationnelle (D5) — dates réelles, distinct du mois facturé.
    nbCheckin,
    nbCheckout,
    // ── Lot 2 ──
    // hebergementsActifs (D4) = nbLogements (= scope.length), jamais null.
    hebergementsActifs: nbLog,
    nbVoyageursMoy,
    occupationHorsBloque: total.occupationHorsBloque,
    nuitsBloquees: total.nuitsBloquees,
    nuitsSansMontant: total.nuitsSansMontant,
    menage: total.menage, // « dont ménage » global
  }

  // ── parPlateforme (camemberts) ──
  const totalCountPf =
    total.parPlateforme.airbnb.nbResa +
    total.parPlateforme.booking.nbResa +
    total.parPlateforme.direct.nbResa
  const totalCaPf = totalRaw.caBrut
  const pct = (num, den) => round2(safeDiv(num, den) * 100)
  const parPlateforme = {
    repartitionResas: {
      airbnb: {
        count: total.parPlateforme.airbnb.nbResa,
        pct: pct(total.parPlateforme.airbnb.nbResa, totalCountPf),
      },
      booking: {
        count: total.parPlateforme.booking.nbResa,
        pct: pct(total.parPlateforme.booking.nbResa, totalCountPf),
      },
      direct: {
        count: total.parPlateforme.direct.nbResa,
        pct: pct(total.parPlateforme.direct.nbResa, totalCountPf),
      },
    },
    caBrutParPlateforme: {
      airbnb: {
        ca: total.parPlateforme.airbnb.caBrut,
        pct: pct(totalRaw.pf.airbnb.caBrut, totalCaPf),
      },
      booking: {
        ca: total.parPlateforme.booking.caBrut,
        pct: pct(totalRaw.pf.booking.caBrut, totalCaPf),
      },
      direct: {
        ca: total.parPlateforme.direct.caBrut,
        pct: pct(totalRaw.pf.direct.caBrut, totalCaPf),
      },
    },
  }

  return { parMois, total, kpis, parPlateforme }
}
